import * as THREE from "three";
import { api } from "./api/client.ts";
import type { BrowseResponse, CameraMode, DriveItem, Measurement, MeasurementType, Note, NoteCategory, Project, RoomPlanData } from "./types.ts";
import { Viewer3D } from "./viewer/Viewer3D.ts";
import type { ClipAxis } from "./viewer/Viewer3D.ts";
import { MeasurePanel } from "./ui/MeasurePanel.ts";
import { NotePanel } from "./ui/NotePanel.ts";
import { InspectorPanel } from "./ui/InspectorPanel.ts";
import { QuickBasePanel } from "./ui/QuickBasePanel.ts";
import { SidePanel } from "./ui/SidePanel.ts";

// ── DOM refs ──────────────────────────────────────────────────────────────────
const pageList = document.getElementById("page-list")!;
const pageViewer = document.getElementById("page-viewer")!;
const projectsGrid = document.getElementById("projects-grid")!;
const emptyState = document.getElementById("empty-state")!;

const btnProjects = document.getElementById("btn-projects")!;
const btnGdrive = document.getElementById("btn-gdrive")!;
const btnUpload = document.getElementById("btn-upload")!;

// Drive browser
const pageDrive = document.getElementById("page-drive")!;
const driveUp = document.getElementById("drive-up")!;
const driveBreadcrumbs = document.getElementById("drive-breadcrumbs")!;
const driveGrid = document.getElementById("drive-grid")!;
const driveEmpty = document.getElementById("drive-empty")!;
const viewerBack = document.getElementById("viewer-back")!;
const loadingOverlay = document.getElementById("loading-overlay")!;
const loadingText = document.getElementById("loading-text")!;
const canvasContainer = document.getElementById("canvas-container")!;

// Viewer toolbar
const modeDollhouse = document.getElementById("mode-dollhouse")!;
const modeWalkthrough = document.getElementById("mode-walkthrough")!;
const modeFloorplan = document.getElementById("mode-floorplan")!;
const btnMeasure = document.getElementById("btn-measure")!;
const btnNotes = document.getElementById("btn-notes")!;

// Measure sub-toolbar
const measureToolbar = document.getElementById("measure-toolbar")!;
const measureHint = document.getElementById("measure-hint")!;
const mDistance = document.getElementById("m-distance")!;
const mHeight = document.getElementById("m-height")!;
const mArea = document.getElementById("m-area")!;
const mPoint = document.getElementById("m-point")!;

// Notes sub-toolbar
const notesToolbar = document.getElementById("notes-toolbar")!;
const notesHint = document.getElementById("notes-hint")!;
const nRoom = document.getElementById("n-room")!;
const nNote = document.getElementById("n-note")!;
const nDamage = document.getElementById("n-damage")!;

// Clip plane controls
const btnClip = document.getElementById("btn-clip")!;
const clipToolbar = document.getElementById("clip-toolbar")!;
const clipX = document.getElementById("clip-x")!;
const clipY = document.getElementById("clip-y")!;
const clipZ = document.getElementById("clip-z")!;

// Plan mode controls
const planControls = document.getElementById("plan-controls")!;
const planCeilingSlider = document.getElementById("plan-ceiling-slider") as HTMLInputElement;
const planCeilingValue = document.getElementById("plan-ceiling-value")!;

// Viewer actions (in toolbar, right-aligned)
const toolbarViewerActions = document.getElementById("toolbar-viewer-actions")!;
const btnStreetview = document.getElementById("btn-streetview")!;
const btnPano360 = document.getElementById("btn-pano360")! as HTMLButtonElement;
const btnPanoAlign = document.getElementById("btn-pano-align")! as HTMLButtonElement;
const btnPanoBack = document.getElementById("btn-pano-back")! as HTMLButtonElement;
const btnMinimap = document.getElementById("btn-minimap")!;
const btnFloorplan = document.getElementById("btn-floorplan")!;
const btnToggleMeasures = document.getElementById("btn-toggle-measures")!;
const btnToggleNotes = document.getElementById("btn-toggle-notes")!;
const btnScreenshot = document.getElementById("btn-screenshot")!;
const btnSidePanel = document.getElementById("btn-side-panel")!;
const btnRoomplan = document.getElementById("btn-roomplan")! as HTMLButtonElement;
const btnAlign = document.getElementById("btn-align")! as HTMLButtonElement;

// Align toolbar
const alignToolbar = document.getElementById("align-toolbar")!;
const alignHint = document.getElementById("align-hint")!;
const alignUndo = document.getElementById("align-undo")!;
const alignReset = document.getElementById("align-reset")!;
const alignApply = document.getElementById("align-apply")! as HTMLButtonElement;
const alignCancel = document.getElementById("align-cancel")!;
const alignClear = document.getElementById("align-clear")! as HTMLButtonElement;

// Modals
const modalBackdrop = document.getElementById("upload-modal-backdrop")!;
const modalCancel = document.getElementById("modal-cancel")!;
const modalSubmit = document.getElementById("modal-submit")!;
const uploadName = document.getElementById("upload-name") as HTMLInputElement;
const dropZone = document.getElementById("drop-zone")!;
const uploadProgress = document.getElementById("upload-progress")!;
const progressBar = document.getElementById("progress-bar") as HTMLProgressElement;
const confirmBackdrop = document.getElementById("confirm-backdrop")!;
const confirmCancel = document.getElementById("confirm-cancel")!;
const confirmOk = document.getElementById("confirm-ok")!;
const toast = document.getElementById("toast")!;

// ── State ─────────────────────────────────────────────────────────────────────
let viewer: Viewer3D | null = null;
let sidePanel: SidePanel | null = null;
let measurePanel: MeasurePanel | null = null;
let notePanel: NotePanel | null = null;
let inspectorPanel: InspectorPanel | null = null;
let quickbasePanel: QuickBasePanel | null = null;
let currentProject: Project | null = null;
let measurements: Measurement[] = [];
let notes: Note[] = [];
let measuresVisible = true;
let notesVisible = true;
let selectedFile: File | null = null;
let confirmCallback: (() => void) | null = null;

// Drive browser state
type BreadcrumbEntry = { id: string; name: string };
let driveStack: BreadcrumbEntry[] = [];
let driveParentId: string | null = null;
let viewerSource: "list" | "drive" = "list";

// ── Navigation ────────────────────────────────────────────────────────────────
function showPage(name: "list" | "drive" | "viewer"): void {
  pageList.classList.toggle("active", name === "list");
  pageDrive.classList.toggle("active", name === "drive");
  pageViewer.classList.toggle("active", name === "viewer");

  // Show upload only in Projects page
  btnUpload.style.display = name === "list" ? "" : "none";

  // Show back button and viewer actions only in Viewer
  viewerBack.style.display = name === "viewer" ? "" : "none";
  toolbarViewerActions.style.display = name === "viewer" ? "flex" : "none";

  // Highlight active nav button
  btnProjects.classList.toggle("active", name === "list");
  btnGdrive.classList.toggle("active", name === "drive");
}

btnProjects.addEventListener("click", () => { showPage("list"); teardownViewer(); loadProjects(); });
btnGdrive.addEventListener("click", () => { showPage("drive"); teardownViewer(); browseDriveRoot(); });
viewerBack.addEventListener("click", () => {
  showPage(viewerSource);
  teardownViewer();
  if (viewerSource === "list") loadProjects();
});

// ── Mode buttons ──────────────────────────────────────────────────────────────
function setActiveMode(mode: CameraMode): void {
  [modeDollhouse, modeWalkthrough, modeFloorplan].forEach((b) => b.classList.remove("active"));
  ({ dollhouse: modeDollhouse, walkthrough: modeWalkthrough, floorplan: modeFloorplan })[mode]
    .classList.add("active");
}

modeDollhouse.addEventListener("click", async () => {
  await viewer?.setMode("dollhouse");
  setActiveMode("dollhouse");
  _syncSectionSlider();
});
modeWalkthrough.addEventListener("click", async () => {
  await viewer?.setMode("walkthrough");
  setActiveMode("walkthrough");
  _syncSectionSlider();
  showToast("Click canvas to enable mouse look · WASD to move");
});
modeFloorplan.addEventListener("click", async () => {
  await viewer?.setMode("floorplan");
  setActiveMode("floorplan");
  // Hide clip toolbar (not relevant in plan mode)
  if (viewer?.isClipModeActive) {
    viewer.setClipModeActive(false);
    btnClip.classList.remove("active");
    clipToolbar.classList.remove("visible");
    [clipX, clipY, clipZ].forEach((b) => b.classList.remove("active"));
  }
  // Always show section slider in plan mode
  planControls.classList.add("visible");
  planCeilingSlider.value = "85";
  planCeilingValue.textContent = "85%";
});

/** Show/hide the section slider based on current state. */
function _syncSectionSlider(): void {
  const show = viewer?.isClipModeActive ?? false;
  planControls.classList.toggle("visible", show);
}

// ── Clip plane mode ──────────────────────────────────────────────────────────
btnClip.addEventListener("click", () => {
  if (!viewer) return;
  const newState = !viewer.isClipModeActive;
  viewer.setClipModeActive(newState);
  btnClip.classList.toggle("active", newState);
  clipToolbar.classList.toggle("visible", newState);
  planControls.classList.toggle("visible", newState);
  if (!newState) {
    [clipX, clipY, clipZ].forEach((b) => b.classList.remove("active"));
    // Reset section slider and clear Y clip
    viewer.clearSectionHeight();
    planCeilingSlider.value = "100";
    planCeilingValue.textContent = "100%";
  } else {
    // Auto-enable Y axis so the slider works immediately
    if (!viewer.getClipAxisState("y").enabled) {
      viewer.toggleClipAxis("y");
    }
    _syncClipAxisButtons();
  }
});

function _syncClipAxisButtons(): void {
  if (!viewer) return;
  for (const [btn, axis] of [[clipX, "x"], [clipY, "y"], [clipZ, "z"]] as [HTMLElement, ClipAxis][]) {
    btn.classList.toggle("active", viewer.getClipAxisState(axis).enabled);
  }
}

[
  [clipX, "x"],
  [clipY, "y"],
  [clipZ, "z"],
].forEach(([btn, axis]) => {
  (btn as HTMLElement).addEventListener("click", () => {
    if (!viewer) return;
    viewer.toggleClipAxis(axis as ClipAxis);
    _syncClipAxisButtons();
  });
});

// ── Section slider ──────────────────────────────────────────────────────────
planCeilingSlider.addEventListener("input", () => {
  if (!viewer) return;
  const pct = Number(planCeilingSlider.value);
  planCeilingValue.textContent = pct + "%";
  if (pct >= 100) {
    viewer.clearSectionHeight();
  } else {
    viewer.setSectionHeight(pct);
  }
});

// ── Measure toolbar ───────────────────────────────────────────────────────────
btnMeasure.addEventListener("click", () => {
  // Close notes toolbar if open
  notesToolbar.classList.remove("visible");
  btnNotes.classList.remove("active");
  stopNote();

  measureToolbar.classList.toggle("visible");
  btnMeasure.classList.toggle("active");
  if (!measureToolbar.classList.contains("visible")) stopMeasure();
});

[
  [mDistance, "distance"],
  [mHeight, "height"],
  [mArea, "area"],
  [mPoint, "point"],
].forEach(([btn, type]) => {
  (btn as HTMLElement).addEventListener("click", () => startMeasure(type as MeasurementType));
});

function startMeasure(type: MeasurementType): void {
  if (!viewer) return;
  viewer.startMeasure(type);
  [mDistance, mHeight, mArea, mPoint].forEach((b) => b.classList.remove("active"));
  const map: Record<MeasurementType, HTMLElement> = {
    distance: mDistance, height: mHeight, area: mArea, point: mPoint,
  };
  map[type].classList.add("active");
  const hints: Record<MeasurementType, string> = {
    distance: "Click 2 points · ESC cancel",
    height: "Click bottom then top · ESC cancel",
    area: "Click points · double-click / Enter / right-click to finish · ESC cancel",
    point: "Click a point · ESC cancel",
  };
  measureHint.textContent = hints[type];
}

function stopMeasure(): void {
  viewer?.stopMeasure();
  [mDistance, mHeight, mArea, mPoint].forEach((b) => b.classList.remove("active"));
}

// ── Notes toolbar ────────────────────────────────────────────────────────────
btnNotes.addEventListener("click", () => {
  // Close measure toolbar if open
  measureToolbar.classList.remove("visible");
  btnMeasure.classList.remove("active");
  stopMeasure();

  notesToolbar.classList.toggle("visible");
  btnNotes.classList.toggle("active");
  if (!notesToolbar.classList.contains("visible")) stopNote();
});

[
  [nRoom, "room"],
  [nNote, "note"],
  [nDamage, "damage"],
].forEach(([btn, cat]) => {
  (btn as HTMLElement).addEventListener("click", () => startNote(cat as NoteCategory));
});

function startNote(category: NoteCategory): void {
  if (!viewer) return;
  viewer.startNote(category);
  [nRoom, nNote, nDamage].forEach((b) => b.classList.remove("active"));
  const map: Record<NoteCategory, HTMLElement> = { room: nRoom, note: nNote, damage: nDamage };
  map[category].classList.add("active");
  const hints: Record<NoteCategory, string> = {
    room: "Click on model to mark a room · ESC cancel",
    note: "Click on model to add a note · ESC cancel",
    damage: "Click on model to mark damage · ESC cancel",
  };
  notesHint.textContent = hints[category];
}

function stopNote(): void {
  viewer?.stopNote();
  [nRoom, nNote, nDamage].forEach((b) => b.classList.remove("active"));
}

// ── Floor plan toggle ─────────────────────────────────────────────────────────
btnFloorplan.addEventListener("click", async () => {
  if (!viewer) return;

  // Already loaded — just toggle
  if (viewer.hasFloorPlan) {
    viewer.toggleFloorPlan();
    btnFloorplan.classList.toggle("active", viewer.floorPlanOverlayVisible);
    return;
  }

  if (!currentProject) {
    showToast("Floor plan not available.");
    return;
  }

  // Not loaded yet — check if it's ready on the server
  try {
    const p = await api.projects.get(currentProject.id);
    if (p.has_floorplan) {
      const fpUrl = api.projects.assetUrl(currentProject.id, "floorplan");
      await viewer.loadFloorPlan(fpUrl);
      viewer.toggleFloorPlan();
      btnFloorplan.classList.toggle("active", viewer.floorPlanOverlayVisible);
      showToast("Floor plan loaded.");
    } else {
      showToast("Floor plan SVG not found in Drive folder.");
    }
  } catch {
    showToast("Could not load floor plan.", true);
  }
});

// ── Minimap toggle ───────────────────────────────────────────────────────────
btnMinimap.addEventListener("click", async () => {
  if (!viewer) return;

  // If floor plan not yet loaded, try to fetch it (only when backed by a project)
  if (!viewer.hasFloorPlan && !currentProject) {
    showToast("Minimap requires a floor plan.", true);
    return;
  }
  if (!viewer.hasFloorPlan && currentProject) {
    try {
      const p = await api.projects.get(currentProject.id);
      if (p.has_floorplan) {
        const fpUrl = api.projects.assetUrl(currentProject.id, "floorplan");
        await viewer.loadFloorPlan(fpUrl);
        showToast("Floor plan loaded.");
      } else {
        showToast("Floor plan SVG not found in Drive folder.", true);
        return;
      }
    } catch {
      showToast("Could not load floor plan.", true);
      return;
    }
  }

  if (!viewer.hasFloorPlan) return;
  viewer.setMinimapVisible(!viewer.minimapVisible);
  btnMinimap.classList.toggle("active", viewer.minimapVisible);
});

// ── Streetview toggle ────────────────────────────────────────────────────────
let streetviewMarkersOn = false;
btnStreetview.addEventListener("click", () => {
  if (!viewer) return;
  if (!viewer.hasStreetView) {
    showToast("Street View is not available for this project. Upload a streetview .zip to enable.", true);
    return;
  }
  if (viewer.isStreetViewActive) {
    // Panel/panorama is open → close it and hide markers
    viewer.exitStreetView();
    streetviewMarkersOn = false;
    viewer.setStreetViewMarkersVisible(false);
    btnStreetview.classList.remove("active");
  } else if (streetviewMarkersOn) {
    // Markers visible, panel closed → hide markers
    streetviewMarkersOn = false;
    viewer.setStreetViewMarkersVisible(false);
    btnStreetview.classList.remove("active");
  } else {
    // Markers hidden → show markers (click on one opens panel).
    // Hide the other layer's markers so the floor plan isn't cluttered.
    if (pano360MarkersOn) {
      pano360MarkersOn = false;
      viewer.setPano360MarkersVisible(false);
      btnPano360.classList.remove("active");
    }
    streetviewMarkersOn = true;
    viewer.setStreetViewMarkersVisible(true);
    btnStreetview.classList.add("active");
  }
});

// ── 360° panorama button — dual-layer (equirect Insta360 + legacy pinhole) ──
let pano360MarkersOn = false;

function exitPanoUI(): void {
  btnPano360.classList.remove("active");
  btnPanoBack.style.display = "none";
  pano360MarkersOn = false;
  viewer?.setPano360MarkersVisible(false);
}

btnPano360.addEventListener("click", () => {
  if (!viewer) return;

  // Equirect (Insta360) layer takes priority when available.
  if (viewer.hasPano360) {
    if (viewer.isPano360Active) {
      viewer.exitPano360();
      exitPanoUI();
    } else if (pano360MarkersOn) {
      pano360MarkersOn = false;
      viewer.setPano360MarkersVisible(false);
      btnPano360.classList.remove("active");
    } else {
      if (streetviewMarkersOn) {
        streetviewMarkersOn = false;
        viewer.setStreetViewMarkersVisible(false);
        btnStreetview.classList.remove("active");
      }
      pano360MarkersOn = true;
      viewer.setPano360MarkersVisible(true);
      btnPano360.classList.add("active");
    }
    return;
  }

  // Fallback: pinhole RTABmap pano (legacy direct-enter behaviour).
  if (viewer.hasStreetView) {
    if (viewer.isStreetViewPanoActive) {
      viewer.exitStreetView();
      exitPanoUI();
    } else {
      viewer.enterPanorama();
      btnPano360.classList.add("active");
      btnPanoBack.style.display = "";
    }
    return;
  }

  showToast("Panorama not available for this project.", true);
});

// ── 360° alignment toggle (Phase D) ──────────────────────────────────────────
btnPanoAlign.addEventListener("click", () => {
  if (!viewer || !viewer.hasPano360) {
    showToast("Alignment requires 360° panoramas to be loaded.", true);
    return;
  }
  const turnOn = !viewer.isPano360AlignActive;
  if (turnOn) {
    // Make sure pano360 markers are visible while we align.
    if (streetviewMarkersOn) {
      streetviewMarkersOn = false;
      viewer.setStreetViewMarkersVisible(false);
      btnStreetview.classList.remove("active");
    }
    pano360MarkersOn = true;
    viewer.setPano360MarkersVisible(true);
    btnPano360.classList.add("active");
    showToast("Drag pano markers to reposition · Click marker to set yaw");
  } else {
    // Leaving align mode — close any open pano editor.
    if (viewer.isPano360Active) viewer.exitPano360();
  }
  viewer.setPano360AlignActive(turnOn);
  btnPanoAlign.classList.toggle("active", turnOn);
});

btnPanoBack.addEventListener("click", () => {
  if (!viewer) return;
  if (viewer.isPano360Active) viewer.exitPano360();
  else viewer.exitStreetView();
  exitPanoUI();
});

// ── RoomPlan alignment (manual pair picking) ─────────────────────────────────
function _updateAlignHint(step: "pick_mesh" | "pick_plan", count: number): void {
  const nextIdx = count + 1;
  if (step === "pick_mesh") {
    alignHint.textContent = `Pair ${nextIdx} — click point on 3D mesh`;
  } else {
    alignHint.textContent = `Pair ${nextIdx} — click matching point on plan`;
  }
  alignApply.disabled = count < 2;
}

async function _ensureRoomPlanLoaded(): Promise<boolean> {
  if (!viewer || !currentProject) return false;
  if (viewer.hasRoomPlanLayer) return true;
  if (!currentProject.has_rooms_data) return false;
  try {
    const url = api.projects.assetUrl(currentProject.id, "rooms_data");
    const res = await fetch(url);
    if (!res.ok) return false;
    const data = (await res.json()) as RoomPlanData;
    const glbUrl = currentProject.has_roomplan_model
      ? api.projects.assetUrl(currentProject.id, "roomplan_model")
      : undefined;
    await viewer.loadRoomPlan(data, glbUrl);
    return true;
  } catch {
    return false;
  }
}

function _exitAlignUI(): void {
  alignToolbar.classList.remove("visible");
  btnAlign.classList.remove("active");
  alignApply.disabled = true;
}

btnAlign.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;
  if (alignToolbar.classList.contains("visible")) {
    // Toggle off — same as Cancel.
    viewer.cancelAlignment();
    _exitAlignUI();
    return;
  }
  if (!currentProject.has_floorplan) {
    showToast("Floor plan SVG is required for alignment.", true);
    return;
  }
  const ok = await _ensureRoomPlanLoaded();
  if (!ok) {
    showToast("RoomPlan data is not available for this project.", true);
    return;
  }
  // Make the layer visible so the user can see which RoomPlan is being aligned.
  if (!viewer.roomPlanLayerVisible) {
    viewer.toggleRoomPlanLayer();
    btnRoomplan.classList.toggle("active", viewer.roomPlanLayerVisible);
  }
  const started = viewer.startAlignment(_updateAlignHint);
  if (!started) {
    showToast("Could not start alignment — floor plan not loaded.", true);
    return;
  }
  alignApply.disabled = true;
  alignToolbar.classList.add("visible");
  btnAlign.classList.add("active");
  alignClear.style.display = currentProject.has_alignment ? "" : "none";
});

alignUndo.addEventListener("click", () => viewer?.undoAlignmentPair());
alignReset.addEventListener("click", () => viewer?.resetAlignmentPairs());
alignCancel.addEventListener("click", () => {
  viewer?.cancelAlignment();
  _exitAlignUI();
});

alignApply.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;
  const result = viewer.finishAlignment();
  if (!result) {
    showToast("Need at least 2 pairs. Keep picking.", true);
    return;
  }
  try {
    const saved = await api.alignment.save(currentProject.id, {
      target: result.target,
      source: result.source,
      yaw: result.yaw,
      tx: result.tx,
      tz: result.tz,
      rmse_meters: result.rmse_meters,
      method: result.method,
    });
    viewer.setAlignmentTransform(saved);
    currentProject = { ...currentProject, has_alignment: true };
    _exitAlignUI();
    showToast(`Alignment saved (RMSE ${(result.rmse * 100).toFixed(1)} cm).`);
  } catch (err) {
    showToast(`Could not save alignment: ${err}`, true);
  }
});

alignClear.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;
  try {
    await api.alignment.delete(currentProject.id);
    viewer.clearAlignment();
    currentProject = { ...currentProject, has_alignment: false };
    viewer.cancelAlignment();
    _exitAlignUI();
    showToast("Saved alignment cleared.");
  } catch (err) {
    showToast(`Could not clear alignment: ${err}`, true);
  }
});

// ── RoomPlan 3D layer ────────────────────────────────────────────────────────
btnRoomplan.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;

  if (viewer.hasRoomPlanLayer) {
    viewer.toggleRoomPlanLayer();
    btnRoomplan.classList.toggle("active", viewer.roomPlanLayerVisible);
    return;
  }

  if (!currentProject.has_rooms_data) {
    showToast("RoomPlan data is not available for this project.", true);
    return;
  }

  try {
    const url = api.projects.assetUrl(currentProject.id, "rooms_data");
    const res = await fetch(url);
    if (!res.ok) throw new Error(res.statusText);
    const data = (await res.json()) as RoomPlanData;
    const glbUrl = currentProject.has_roomplan_model
      ? api.projects.assetUrl(currentProject.id, "roomplan_model")
      : undefined;
    await viewer.loadRoomPlan(data, glbUrl);
    viewer.toggleRoomPlanLayer();
    btnRoomplan.classList.toggle("active", viewer.roomPlanLayerVisible);
    showToast("RoomPlan layer loaded.");
  } catch (err) {
    showToast(`Could not load RoomPlan: ${err}`, true);
  }
});

// ── Screenshot ────────────────────────────────────────────────────────────────
btnScreenshot.addEventListener("click", () => {
  if (!viewer) return;
  const dataUrl = viewer.takeScreenshot();
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = `${currentProject?.name ?? "screenshot"}.jpg`;
  a.click();
});

// ── Visibility toggles ───────────────────────────────────────────────────────
btnToggleMeasures.addEventListener("click", () => {
  measuresVisible = !measuresVisible;
  viewer?.setMeasurementsVisible(measuresVisible);
  btnToggleMeasures.classList.toggle("active", measuresVisible);
});

btnToggleNotes.addEventListener("click", () => {
  notesVisible = !notesVisible;
  viewer?.setNotesVisible(notesVisible);
  btnToggleNotes.classList.toggle("active", notesVisible);
});

// ── Side panel ───────────────────────────────────────────────────────────────
btnSidePanel.addEventListener("click", () => {
  if (!sidePanel) return;
  if (sidePanel.isOpen) sidePanel.close();
  else sidePanel.open(sidePanel.activeTab ?? "measures");
});

async function deleteMeasurement(id: string): Promise<void> {
  await api.measurements.delete(id);
  viewer?.removeAnnotation(id);
  measurements = measurements.filter((m) => m.id !== id);
  measurePanel?.render(measurements);
}

async function deleteNote(id: string): Promise<void> {
  await api.notes.delete(id);
  viewer?.removeNoteAnnotation(id);
  notes = notes.filter((n) => n.id !== id);
  notePanel?.render(notes);
}

// ── Project list ──────────────────────────────────────────────────────────────
async function loadProjects(): Promise<void> {
  try {
    const projects = await api.projects.list();
    renderProjects(projects);
  } catch (err) {
    showToast(String(err), true);
  }
}

function renderProjects(projects: Project[]): void {
  projectsGrid.innerHTML = "";
  if (projects.length === 0) { projectsGrid.appendChild(emptyState); return; }

  for (const p of projects) {
    const hasMesh = p.has_glb || p.has_ply;
    const card = document.createElement("div");
    card.className = `project-card${p.status === "ready" && hasMesh ? " clickable" : ""}`;
    card.innerHTML = `
      <div class="card-name" data-id="${p.id}">${escHtml(p.name)}</div>
      <div class="card-meta">${new Date(p.created_at).toLocaleString()}</div>
      <span class="status-badge ${p.status}">${p.status}</span>
      <div class="card-actions">
        <button class="btn" data-rename="${p.id}" title="Rename">✏</button>
        <button class="btn danger" data-delete="${p.id}" title="Delete">🗑</button>
      </div>
    `;

    if (p.status === "ready" && hasMesh) {
      card.querySelector(".card-name")!.addEventListener("click", () => openProject(p));
    }

    card.querySelector<HTMLElement>("[data-rename]")!.addEventListener("click", (e) => {
      e.stopPropagation();
      startRename(p, card);
    });
    card.querySelector<HTMLElement>("[data-delete]")!.addEventListener("click", (e) => {
      e.stopPropagation();
      showConfirm(`Delete "${p.name}"?`, "This permanently deletes the project and all files from Google Drive.", async () => {
        await api.projects.delete(p.id);
        loadProjects();
        showToast("Project deleted.");
      });
    });

    projectsGrid.appendChild(card);
  }
}

function startRename(p: Project, card: HTMLElement): void {
  const nameEl = card.querySelector<HTMLElement>(".card-name")!;
  const input = document.createElement("input");
  input.type = "text";
  input.className = "card-name-input";
  input.value = p.name;
  nameEl.replaceWith(input);
  input.focus();
  input.select();

  const commit = async () => {
    const name = input.value.trim();
    if (name && name !== p.name) {
      await api.projects.rename(p.id, name);
      showToast("Renamed.");
    }
    loadProjects();
  };
  input.addEventListener("blur", commit);
  input.addEventListener("keydown", (e) => { if (e.key === "Enter") input.blur(); if (e.key === "Escape") loadProjects(); });
}

async function openProject(p: Project): Promise<void> {
  viewerSource = "list";
  currentProject = p;
  measurements = [];
  notes = [];
  measuresVisible = true;
  notesVisible = true;
  showPage("viewer");
  loadingOverlay.style.display = "flex";
  loadingText.textContent = "Loading 3D model…";

  teardownViewer();

  modeWalkthrough.removeAttribute("disabled");
  modeFloorplan.removeAttribute("disabled");
  btnMeasure.removeAttribute("disabled");
  btnNotes.removeAttribute("disabled");
  btnToggleMeasures.classList.add("active");
  btnToggleNotes.classList.add("active");
  setActiveMode("dollhouse");
  viewer = new Viewer3D(canvasContainer);

  // Create unified side panel with tabs
  sidePanel = new SidePanel(document.getElementById("side-panel-container")!);
  const measuresPane = sidePanel.addTab("measures", "Measures");
  const notesPane = sidePanel.addTab("notes", "Notes");
  const inspectorPane = sidePanel.addTab("inspector", "Inspector", false);
  const quickbasePane = sidePanel.addTab("quickbase", "QuickBase", false);
  sidePanel.onTabChange = (tab) => {
    btnSidePanel.classList.toggle("active", tab !== null);
    // Pause video when switching away from inspector
    if (tab !== "inspector") inspectorPanel?.pause();
  };

  measurePanel = new MeasurePanel(measuresPane, deleteMeasurement, (id) => viewer?.highlightMeasurement(id));
  notePanel = new NotePanel(notesPane, deleteNote, (id) => viewer?.highlightNote(id));
  inspectorPanel = new InspectorPanel(inspectorPane);
  quickbasePanel = new QuickBasePanel(quickbasePane);

  viewer.onNoteComplete = async (result) => {
    const pt = { x: result.point.x, y: result.point.y, z: result.point.z };
    try {
      const saved = await api.notes.create({
        project_id: p.id,
        category: result.category,
        text: result.text,
        point: pt,
      });
      viewer?.addNoteAnnotation(
        saved.id,
        saved.category,
        new THREE.Vector3(saved.point.x, saved.point.y, saved.point.z),
        saved.text,
      );
      notes.push(saved);
      notePanel?.render(notes);
    } catch (err) {
      showToast(`Could not save note: ${err}`, true);
    }
  };

  viewer.onMeasureComplete = async (result) => {
    const pts = result.points.map((v) => ({ x: v.x, y: v.y, z: v.z }));

    try {
      const saved = await api.measurements.create({
        project_id: p.id,
        type: result.type,
        points: pts,
        value: result.value,
        unit: result.unit,
      });
      viewer?.addAnnotation(
        saved.id,
        saved.type,
        saved.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
        saved.value ?? 0,
        saved.unit ?? "",
        saved.label,
      );
      measurements.push(saved);
      measurePanel?.render(measurements);
    } catch (err) {
      showToast(`Could not save measurement: ${err}`, true);
    }
  };

  try {
    const meshRole = p.mesh_role ?? "glb";
    const meshUrl = api.projects.assetUrl(p.id, meshRole);
    const fpUrl = p.has_floorplan ? api.projects.assetUrl(p.id, "floorplan") : undefined;
    await viewer.loadProject(meshUrl, fpUrl, meshRole);
    loadingOverlay.style.display = "none";
    if (p.has_rooms_data) btnRoomplan.removeAttribute("disabled");
    if (p.has_rooms_data && p.has_floorplan) btnAlign.removeAttribute("disabled");
    if (p.has_alignment) {
      try {
        const align = await api.alignment.get(p.id);
        if (align) viewer.setAlignmentTransform(align);
      } catch {
        // non-fatal
      }
    }

    // Load existing measurements
    measurements = await api.measurements.list(p.id);
    for (const m of measurements) {
      viewer.addAnnotation(
        m.id, m.type,
        m.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
        m.value ?? 0, m.unit ?? "", m.label,
      );
    }
    measurePanel?.render(measurements);

    // Load existing notes
    notes = await api.notes.list(p.id);
    for (const n of notes) {
      viewer.addNoteAnnotation(
        n.id, n.category,
        new THREE.Vector3(n.point.x, n.point.y, n.point.z),
        n.text,
      );
    }
    notePanel?.render(notes);

    // Try loading QuickBase inspection record
    if (quickbasePanel && sidePanel && p.gdrive_folder_id) {
      try {
        console.log("[QuickBase] looking up folder", p.gdrive_folder_id);
        const found = await quickbasePanel.load(p.gdrive_folder_id);
        sidePanel.setTabVisible("quickbase", found);
        console.log("[QuickBase]", found ? "record found" : "no record");
      } catch (qbErr) {
        console.log("[QuickBase] not available:", qbErr);
      }
    }

    // Try loading inspection evidence
    if (inspectorPanel && sidePanel) {
      try {
        console.log("[Inspection] fetching data for project", p.id);
        const data = await api.inspection.load(p.id);
        inspectorPanel.load(data, (f) => api.inspection.mediaUrl(p.id, f));
        sidePanel.setTabVisible("inspector", true);
        console.log("[Inspection] loaded OK:", data.segments.length, "segments,", data.videos.length, "videos");
      } catch (inspErr) {
        console.log("[Inspection] not available:", inspErr);
      }
    }

    // Streetview + pano360: independent layers, both auto-fetched from Drive.
    viewer.onPanoExit = () => exitPanoUI();
    viewer.onPano360Enter = () => {
      btnPano360.classList.add("active");
      btnPanoBack.style.display = "";
    };

    try {
      const manifestUrl = api.streetview.manifestUrl(p.id);
      const imageBaseUrl = `/api/projects/${p.id}/streetview/images`;
      console.log(`[StreetView] project ${p.id}: loading manifest...`);
      await viewer.loadStreetView(manifestUrl, imageBaseUrl);
      btnStreetview.removeAttribute("disabled");
      btnPano360.removeAttribute("disabled"); // legacy pinhole-pano fallback
      console.log("[StreetView] loaded OK");
    } catch (svErr) {
      console.log("[StreetView] not available:", svErr);
    }

    try {
      const manifestUrl = api.pano360.manifestUrl(p.id);
      const imageBaseUrl = api.pano360.imageBaseUrl(p.id);
      console.log(`[Pano360] project ${p.id}: loading manifest...`);
      await viewer.loadPano360(p.id, manifestUrl, imageBaseUrl);
      btnPano360.removeAttribute("disabled");
      btnPanoAlign.removeAttribute("disabled");
      console.log("[Pano360] loaded OK");
    } catch (panoErr) {
      console.log("[Pano360] not available:", panoErr);
    }
  } catch (err) {
    loadingText.textContent = `Failed to load: ${err}`;
    showToast(String(err), true);
  }
}

function teardownViewer(): void {
  sidePanel?.dispose();
  sidePanel = null;
  measurePanel = null;
  notePanel = null;
  inspectorPanel = null;
  viewer?.dispose();
  viewer = null;
  currentProject = null;
  measurements = [];
  notes = [];
  measureToolbar.classList.remove("visible");
  notesToolbar.classList.remove("visible");
  btnMeasure.classList.remove("active");
  btnNotes.classList.remove("active");
  modeWalkthrough.setAttribute("disabled", "");
  modeFloorplan.setAttribute("disabled", "");
  btnMeasure.setAttribute("disabled", "");
  btnNotes.setAttribute("disabled", "");
  btnMinimap.classList.remove("active");
  btnFloorplan.classList.remove("active");
  btnRoomplan.classList.remove("active");
  btnRoomplan.setAttribute("disabled", "");
  btnAlign.classList.remove("active");
  btnAlign.setAttribute("disabled", "");
  alignToolbar.classList.remove("visible");
  btnStreetview.classList.remove("active");
  btnStreetview.setAttribute("disabled", "");
  btnPano360.setAttribute("disabled", "");
  btnPano360.classList.remove("active");
  btnPanoAlign.setAttribute("disabled", "");
  btnPanoAlign.classList.remove("active");
  btnPanoBack.style.display = "none";
  streetviewMarkersOn = false;
  pano360MarkersOn = false;
  btnToggleMeasures.classList.remove("active");
  btnToggleNotes.classList.remove("active");
  btnSidePanel.classList.remove("active");
  // Reset clip mode
  btnClip.classList.remove("active");
  clipToolbar.classList.remove("visible");
  [clipX, clipY, clipZ].forEach((b) => b.classList.remove("active"));
  planControls.classList.remove("visible");
}

// ── Drive browser ────────────────────────────────────────────────────────────

async function browseDriveRoot(): Promise<void> {
  driveStack = [];
  driveParentId = null;
  await browseDriveFolder();
}

async function browseDriveFolder(folderId?: string): Promise<void> {
  driveGrid.innerHTML = "";
  driveEmpty.textContent = "Loading...";
  driveGrid.appendChild(driveEmpty);

  try {
    const resp: BrowseResponse = await api.gdrive.browse(folderId);

    // Update breadcrumb stack
    if (folderId) {
      const idx = driveStack.findIndex((e) => e.id === folderId);
      if (idx >= 0) {
        driveStack = driveStack.slice(0, idx + 1);
      } else {
        driveStack.push({ id: resp.folder_id, name: resp.folder_name });
      }
    } else {
      driveStack = [{ id: resp.folder_id, name: resp.folder_name }];
    }

    driveParentId = resp.parent_id;
    driveUp.style.display = driveParentId ? "" : "none";

    renderBreadcrumbs();
    renderDriveItems(resp.items);
  } catch (err) {
    showToast(String(err), true);
  }
}

driveUp.addEventListener("click", () => {
  if (driveStack.length > 1) {
    driveStack.pop();
    browseDriveFolder(driveStack[driveStack.length - 1].id);
  } else if (driveParentId) {
    driveStack = [];
    browseDriveFolder(driveParentId);
  }
});

function renderBreadcrumbs(): void {
  driveBreadcrumbs.innerHTML = "";
  for (let i = 0; i < driveStack.length; i++) {
    const entry = driveStack[i];
    if (i > 0) {
      const sep = document.createElement("span");
      sep.className = "sep";
      sep.textContent = " / ";
      driveBreadcrumbs.appendChild(sep);
    }
    if (i < driveStack.length - 1) {
      const crumb = document.createElement("span");
      crumb.className = "crumb";
      crumb.textContent = entry.name;
      crumb.addEventListener("click", () => browseDriveFolder(entry.id));
      driveBreadcrumbs.appendChild(crumb);
    } else {
      const current = document.createElement("span");
      current.textContent = entry.name;
      current.style.color = "var(--text)";
      current.style.fontWeight = "600";
      driveBreadcrumbs.appendChild(current);
    }
  }
}

function renderDriveItems(items: DriveItem[]): void {
  driveGrid.innerHTML = "";
  if (items.length === 0) {
    driveEmpty.textContent = "No folders or 3D files (.glb, .ply) in this folder.";
    driveGrid.appendChild(driveEmpty);
    return;
  }

  // Sort: folders first, then files alphabetically
  const sorted = [...items].sort((a, b) => {
    if (a.is_folder !== b.is_folder) return a.is_folder ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  for (const item of sorted) {
    const el = document.createElement("div");
    el.className = "drive-item";
    const sizeText = item.size_bytes ? `<div class="item-size">${formatBytes(item.size_bytes)}</div>` : "";
    el.innerHTML = `
      <span class="icon">${item.is_folder ? "\uD83D\uDCC1" : "\uD83D\uDCCE"}</span>
      <div>
        <div class="item-name">${escHtml(item.name)}</div>
        ${sizeText}
      </div>
    `;

    if (item.is_folder) {
      el.addEventListener("click", () => browseDriveFolder(item.id));
    } else {
      el.addEventListener("click", () => openDriveFile(item));
    }

    driveGrid.appendChild(el);
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function openDriveFile(item: DriveItem): Promise<void> {
  viewerSource = "drive";
  currentProject = null;
  measurements = [];
  notes = [];
  measuresVisible = true;
  notesVisible = true;
  showPage("viewer");
  loadingOverlay.style.display = "flex";
  loadingText.textContent = "Loading 3D model from Google Drive...";

  teardownViewer();

  modeWalkthrough.removeAttribute("disabled");
  modeFloorplan.removeAttribute("disabled");
  btnMeasure.removeAttribute("disabled");
  btnNotes.removeAttribute("disabled");
  btnToggleMeasures.classList.add("active");
  btnToggleNotes.classList.add("active");
  setActiveMode("dollhouse");
  viewer = new Viewer3D(canvasContainer);

  // Create unified side panel with tabs
  sidePanel = new SidePanel(document.getElementById("side-panel-container")!);
  const measuresPane2 = sidePanel.addTab("measures", "Measures");
  const notesPane2 = sidePanel.addTab("notes", "Notes");
  const inspectorPane2 = sidePanel.addTab("inspector", "Inspector", false);
  const quickbasePane2 = sidePanel.addTab("quickbase", "QuickBase", false);
  sidePanel.onTabChange = (tab) => {
    btnSidePanel.classList.toggle("active", tab !== null);
    if (tab !== "inspector") inspectorPanel?.pause();
  };

  measurePanel = new MeasurePanel(measuresPane2, deleteMeasurement, (id) => viewer?.highlightMeasurement(id));
  notePanel = new NotePanel(notesPane2, deleteNote, (id) => viewer?.highlightNote(id));
  inspectorPanel = new InspectorPanel(inspectorPane2);
  quickbasePanel = new QuickBasePanel(quickbasePane2);

  // Build folder path string for project name
  const folderPath = driveStack.map((e) => e.name).join(" / ");

  // Kick off background project creation (don't await before loading viewer)
  const projectPromise = api.gdrive.open(item.id, item.name, folderPath);

  try {
    const meshUrl = api.gdrive.streamUrl(item.id);
    const format = item.name.toLowerCase().endsWith(".glb") ? "glb" : "ply";
    await viewer.loadProject(meshUrl, undefined, format);
    loadingOverlay.style.display = "none";

    // Once project is created, wire up measurements & notes
    const result = await projectPromise;
    const project = await api.projects.get(result.project_id);
    currentProject = project;
    if (project.has_rooms_data) btnRoomplan.removeAttribute("disabled");
    if (project.has_rooms_data && project.has_floorplan) btnAlign.removeAttribute("disabled");
    if (project.has_alignment) {
      try {
        const align = await api.alignment.get(project.id);
        if (align) viewer?.setAlignmentTransform(align);
      } catch {
        // non-fatal
      }
    }

    viewer.onMeasureComplete = async (measureResult) => {
      const pts = measureResult.points.map((v) => ({ x: v.x, y: v.y, z: v.z }));
      try {
        const saved = await api.measurements.create({
          project_id: project.id,
          type: measureResult.type,
          points: pts,
          value: measureResult.value,
          unit: measureResult.unit,
        });
        viewer?.addAnnotation(
          saved.id,
          saved.type,
          saved.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
          saved.value ?? 0,
          saved.unit ?? "",
          saved.label,
        );
        measurements.push(saved);
        measurePanel?.render(measurements);
      } catch (err) {
        showToast(`Could not save measurement: ${err}`, true);
      }
    };

    viewer.onNoteComplete = async (noteResult) => {
      const pt = { x: noteResult.point.x, y: noteResult.point.y, z: noteResult.point.z };
      try {
        const saved = await api.notes.create({
          project_id: project.id,
          category: noteResult.category,
          text: noteResult.text,
          point: pt,
        });
        viewer?.addNoteAnnotation(
          saved.id,
          saved.category,
          new THREE.Vector3(saved.point.x, saved.point.y, saved.point.z),
          saved.text,
        );
        notes.push(saved);
        notePanel?.render(notes);
      } catch (err) {
        showToast(`Could not save note: ${err}`, true);
      }
    };

    // Load existing measurements for this project
    measurements = await api.measurements.list(project.id);
    for (const m of measurements) {
      viewer?.addAnnotation(
        m.id, m.type,
        m.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
        m.value ?? 0, m.unit ?? "", m.label,
      );
    }
    measurePanel?.render(measurements);

    // Load existing notes for this project
    notes = await api.notes.list(project.id);
    for (const n of notes) {
      viewer?.addNoteAnnotation(
        n.id, n.category,
        new THREE.Vector3(n.point.x, n.point.y, n.point.z),
        n.text,
      );
    }
    notePanel?.render(notes);

    // Try loading QuickBase inspection record
    if (quickbasePanel && sidePanel && driveStack.length > 0) {
      const currentFolderId = driveStack[driveStack.length - 1].id;
      try {
        console.log("[QuickBase] looking up folder", currentFolderId);
        const found = await quickbasePanel.load(currentFolderId);
        sidePanel.setTabVisible("quickbase", found);
        console.log("[QuickBase]", found ? "record found" : "no record");
      } catch (qbErr) {
        console.log("[QuickBase] not available:", qbErr);
      }
    }

    // Try loading inspection evidence
    if (inspectorPanel && sidePanel) {
      try {
        console.log("[Inspection] fetching data for drive project", project.id);
        const data = await api.inspection.load(project.id);
        inspectorPanel.load(data, (f) => api.inspection.mediaUrl(project.id, f));
        sidePanel.setTabVisible("inspector", true);
        console.log("[Inspection] loaded OK:", data.segments.length, "segments,", data.videos.length, "videos");
      } catch (inspErr) {
        console.log("[Inspection] not available:", inspErr);
      }
    }

    // Streetview + pano360: independent layers, both auto-fetched from Drive.
    viewer.onPanoExit = () => exitPanoUI();
    viewer.onPano360Enter = () => {
      btnPano360.classList.add("active");
      btnPanoBack.style.display = "";
    };

    try {
      const manifestUrl = api.streetview.manifestUrl(project.id);
      const imageBaseUrl = `/api/projects/${project.id}/streetview/images`;
      console.log(`[StreetView] project ${project.id}: loading manifest...`);
      await viewer.loadStreetView(manifestUrl, imageBaseUrl);
      btnStreetview.removeAttribute("disabled");
      btnPano360.removeAttribute("disabled"); // legacy pinhole-pano fallback
      console.log("[StreetView] loaded OK");
    } catch (svErr) {
      console.log("[StreetView] not available:", svErr);
    }

    try {
      const manifestUrl = api.pano360.manifestUrl(project.id);
      const imageBaseUrl = api.pano360.imageBaseUrl(project.id);
      console.log(`[Pano360] project ${project.id}: loading manifest...`);
      await viewer.loadPano360(project.id, manifestUrl, imageBaseUrl);
      btnPano360.removeAttribute("disabled");
      btnPanoAlign.removeAttribute("disabled");
      console.log("[Pano360] loaded OK");
    } catch (panoErr) {
      console.log("[Pano360] not available:", panoErr);
    }

    showToast("Project loaded.");
  } catch (err) {
    loadingText.textContent = `Failed to load: ${err}`;
    showToast(String(err), true);
  }
}

// ── Upload modal ──────────────────────────────────────────────────────────────
btnUpload.addEventListener("click", openModal);
modalCancel.addEventListener("click", closeModal);
modalBackdrop.addEventListener("click", (e) => { if (e.target === modalBackdrop) closeModal(); });

function openModal(): void {
  modalBackdrop.classList.add("open");
  uploadName.value = "";
  selectedFile = null;
  resetDropZone();
  uploadProgress.style.display = "none";
  progressBar.value = 0;
  modalSubmit.setAttribute("disabled", "");
}
function closeModal(): void { modalBackdrop.classList.remove("open"); }

function resetDropZone(): void {
  dropZone.innerHTML = `
    <div>Drop .glb or .ply file here</div>
    <div style="font-size:12px;margin-top:4px">or click to browse</div>
    <input type="file" id="file-input" accept=".glb,.ply" style="display:none" />
  `;
  dropZone.classList.remove("has-file", "drag-over");
  dropZone.querySelector<HTMLInputElement>("#file-input")!
    .addEventListener("change", (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (f) setSelectedFile(f);
    });
}

dropZone.addEventListener("click", () => dropZone.querySelector<HTMLInputElement>("#file-input")?.click());
dropZone.addEventListener("dragover", (e) => { e.preventDefault(); dropZone.classList.add("drag-over"); });
dropZone.addEventListener("dragleave", () => dropZone.classList.remove("drag-over"));
dropZone.addEventListener("drop", (e) => {
  e.preventDefault();
  dropZone.classList.remove("drag-over");
  const f = e.dataTransfer?.files[0];
  if (f) setSelectedFile(f);
});

function setSelectedFile(f: File): void {
  selectedFile = f;
  if (!uploadName.value) uploadName.value = f.name.replace(/\.[^.]+$/, "");
  dropZone.classList.add("has-file");
  dropZone.querySelector("div")!.textContent = `Selected: ${f.name}`;
  updateSubmitState();
}
uploadName.addEventListener("input", updateSubmitState);
function updateSubmitState(): void {
  if (selectedFile && uploadName.value.trim()) modalSubmit.removeAttribute("disabled");
  else modalSubmit.setAttribute("disabled", "");
}

modalSubmit.addEventListener("click", async () => {
  if (!selectedFile || !uploadName.value.trim()) return;
  modalSubmit.setAttribute("disabled", "");
  uploadProgress.style.display = "block";
  try {
    await api.upload(selectedFile, uploadName.value.trim(), (pct) => { progressBar.value = pct; });
    closeModal();
    showToast("Upload complete — project is ready.");
    loadProjects();
  } catch (err) {
    showToast(String(err), true);
    modalSubmit.removeAttribute("disabled");
  }
});

// ── Confirm dialog ────────────────────────────────────────────────────────────
function showConfirm(title: string, body: string, onOk: () => void): void {
  document.getElementById("confirm-title")!.textContent = title;
  document.getElementById("confirm-body")!.textContent = body;
  confirmCallback = onOk;
  confirmBackdrop.classList.add("open");
}
confirmCancel.addEventListener("click", () => confirmBackdrop.classList.remove("open"));
confirmBackdrop.addEventListener("click", (e) => { if (e.target === confirmBackdrop) confirmBackdrop.classList.remove("open"); });
confirmOk.addEventListener("click", () => {
  confirmBackdrop.classList.remove("open");
  confirmCallback?.();
  confirmCallback = null;
});

// ── Toast ─────────────────────────────────────────────────────────────────────
let toastTimer: ReturnType<typeof setTimeout> | null = null;
function showToast(msg: string, error = false): void {
  toast.textContent = msg;
  toast.classList.toggle("error", error);
  toast.classList.add("show");
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove("show"), 4500);
}

// ── Utils ─────────────────────────────────────────────────────────────────────
function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// ── Embed mode ────────────────────────────────────────────────────────────────
// Activated when the page is loaded with `?embed=1` (or `?mesh=...`). The viewer
// loads a single .glb/.ply directly from the URL, with no project list, no Drive
// browser, no upload flow, and no backend persistence (measurements/notes live in
// memory only). A `?buttons=` allowlist controls which toolbar controls are shown.
//
// Supported URL params:
//   embed=1                — explicit toggle (optional if `mesh` is present)
//   mesh=<url>             — required: URL to the .glb / .ply
//   format=glb|ply         — optional, inferred from URL extension by default
//   floorplan=<url>        — optional: URL to floor plan SVG
//   buttons=k1,k2,...      — optional: only show these toolbar buttons; if omitted,
//                            all buttons are shown. Keys: dollhouse, walk, plan,
//                            modes (= all 3 modes), measure, notes, clip, pano360,
//                            panoalign, streetview, minimap, floorplan,
//                            togglemeasures, togglenotes, roomplan, align,
//                            screenshot, sidepanel
//   name=<string>          — optional: shown in the document title
//
// Example:
//   /viewer/?mesh=/assets/demo-scan.glb&buttons=modes,measure,minimap,screenshot

interface EmbedConfig {
  meshUrl: string;
  meshFormat: "glb" | "ply";
  floorPlanUrl?: string;
  allowedButtons?: Set<string>;
  name?: string;
}

/** Map of toolbar element id → set of short keys it satisfies. */
const EMBED_BUTTON_KEYS: Record<string, string[]> = {
  "mode-dollhouse": ["dollhouse", "modes"],
  "mode-walkthrough": ["walk", "walkthrough", "modes"],
  "mode-floorplan": ["plan", "modes"],
  "btn-measure": ["measure"],
  "btn-notes": ["notes"],
  "btn-clip": ["clip"],
  "btn-pano360": ["pano360", "pano"],
  "btn-pano-align": ["panoalign"],
  "btn-streetview": ["streetview"],
  "btn-minimap": ["minimap"],
  "btn-floorplan": ["floorplan"],
  "btn-toggle-measures": ["togglemeasures"],
  "btn-toggle-notes": ["togglenotes"],
  "btn-roomplan": ["roomplan"],
  "btn-align": ["align"],
  "btn-screenshot": ["screenshot"],
  "btn-side-panel": ["sidepanel"],
};

function _parseEmbedConfig(params: URLSearchParams): EmbedConfig | null {
  const meshUrl = params.get("mesh");
  if (!meshUrl) return null;
  const explicitFormat = params.get("format");
  const meshFormat: "glb" | "ply" = explicitFormat === "ply"
    ? "ply"
    : explicitFormat === "glb"
      ? "glb"
      : meshUrl.toLowerCase().endsWith(".ply")
        ? "ply"
        : "glb";
  const floorPlanUrl = params.get("floorplan") ?? undefined;
  const buttonsParam = params.get("buttons");
  const allowedButtons = buttonsParam
    ? new Set(buttonsParam.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean))
    : undefined;
  const name = params.get("name") ?? undefined;
  return { meshUrl, meshFormat, floorPlanUrl, allowedButtons, name };
}

function _applyEmbedButtonAllowlist(allow: Set<string> | undefined): void {
  if (!allow) return;
  for (const [id, keys] of Object.entries(EMBED_BUTTON_KEYS)) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (!keys.some((k) => allow.has(k))) {
      el.style.display = "none";
    }
  }
  // Hide the in-toolbar separator if no measure/notes buttons survived.
  const sepHasNeighbours = !!(
    document.getElementById("btn-measure")?.style.display !== "none" ||
    document.getElementById("btn-notes")?.style.display !== "none"
  );
  if (!sepHasNeighbours) {
    const sep = document.querySelector<HTMLElement>("#viewer-toolbar > div[style*='width:1px']");
    if (sep) sep.style.display = "none";
  }
}

async function openEmbed(cfg: EmbedConfig): Promise<void> {
  viewerSource = "list";
  currentProject = null;
  measurements = [];
  notes = [];
  measuresVisible = true;
  notesVisible = true;

  showPage("viewer");
  // No back button in embed (nothing to go back to).
  viewerBack.style.display = "none";

  loadingOverlay.style.display = "flex";
  loadingText.textContent = "Loading 3D model…";

  teardownViewer();

  modeWalkthrough.removeAttribute("disabled");
  modeFloorplan.removeAttribute("disabled");
  btnMeasure.removeAttribute("disabled");
  btnNotes.removeAttribute("disabled");
  btnToggleMeasures.classList.add("active");
  btnToggleNotes.classList.add("active");
  setActiveMode("dollhouse");

  viewer = new Viewer3D(canvasContainer);

  // Side panel — only the Measures + Notes tabs (no backend-only Inspector / QuickBase).
  sidePanel = new SidePanel(document.getElementById("side-panel-container")!);
  const measuresPane = sidePanel.addTab("measures", "Measures");
  const notesPane = sidePanel.addTab("notes", "Notes");
  sidePanel.onTabChange = (tab) => {
    btnSidePanel.classList.toggle("active", tab !== null);
  };

  measurePanel = new MeasurePanel(
    measuresPane,
    async (id) => {
      viewer?.removeAnnotation(id);
      measurements = measurements.filter((m) => m.id !== id);
      measurePanel?.render(measurements);
    },
    (id) => viewer?.highlightMeasurement(id),
  );
  notePanel = new NotePanel(
    notesPane,
    async (id) => {
      viewer?.removeNoteAnnotation(id);
      notes = notes.filter((n) => n.id !== id);
      notePanel?.render(notes);
    },
    (id) => viewer?.highlightNote(id),
  );

  // Memory-only persistence — no /api/measurements, no /api/notes.
  let nextEmbedId = 1;
  const genEmbedId = (): string => `embed-${nextEmbedId++}`;

  viewer.onMeasureComplete = (result) => {
    const id = genEmbedId();
    viewer?.addAnnotation(id, result.type, result.points, result.value, result.unit);
    measurements.push({
      id,
      project_id: "embed",
      type: result.type,
      points: result.points.map((v) => ({ x: v.x, y: v.y, z: v.z })),
      value: result.value,
      unit: result.unit,
      label: null,
      created_at: new Date().toISOString(),
    });
    measurePanel?.render(measurements);
  };

  viewer.onNoteComplete = (result) => {
    const id = genEmbedId();
    viewer?.addNoteAnnotation(id, result.category, result.point, result.text);
    notes.push({
      id,
      project_id: "embed",
      category: result.category,
      text: result.text,
      point: { x: result.point.x, y: result.point.y, z: result.point.z },
      created_at: new Date().toISOString(),
    });
    notePanel?.render(notes);
  };

  if (cfg.name) document.title = cfg.name;

  try {
    await viewer.loadProject(cfg.meshUrl, cfg.floorPlanUrl, cfg.meshFormat);
    loadingOverlay.style.display = "none";
  } catch (err) {
    loadingText.textContent = `Failed to load: ${err}`;
    showToast(String(err), true);
  }
}

function _initEmbed(cfg: EmbedConfig): void {
  // Drop the entire 52px top header bar in embed mode — none of its contents
  // (brand, Projects/Drive nav, viewer-actions group) belong on a website embed.
  // Collapse the #app grid row so the viewer fills the full iframe.
  const toolbar = document.getElementById("toolbar");
  if (toolbar) toolbar.style.display = "none";
  const app = document.getElementById("app");
  if (app) app.style.gridTemplateRows = "1fr";

  _applyEmbedButtonAllowlist(cfg.allowedButtons);

  void openEmbed(cfg);
}

// ── Init ──────────────────────────────────────────────────────────────────────
const _params = new URLSearchParams(window.location.search);
const _embedCfg = _params.get("embed") === "1" || _params.has("mesh")
  ? _parseEmbedConfig(_params)
  : null;

if (_embedCfg) {
  _initEmbed(_embedCfg);
} else {
  browseDriveRoot();
}
